// @flow

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PloSwitch from 'components/PloSwitch/ploswitch';
import { menuLinks } from '../../db/links';
import blackDiscord from '../../assets/svg/blackDiscord.svg';
import styles from './styles.module.scss';

type Props = {
  isVisible: boolean,
  setVisible: (boolean) => void,
  onClose?: () => void,
  isPlo5: boolean,
}

const MobileMenu = ({
  isVisible, setVisible, onClose, isPlo5,
}: Props) => {
  const menuRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isVisible]);

  const close = () => {
    if (onClose) onClose();
    else setVisible(false);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
    if (event.key !== 'Tab' || !menuRef.current) return;
    const items = menuRef.current.querySelectorAll('a[href], button:not([disabled])');
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isVisible) return null;

  return (
    // The overlay contains keyboard focus until navigation or dismissal.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <nav
      ref={menuRef}
      id="mobile-navigation"
      aria-label="Main navigation"
      className={styles.menuContainer}
      onKeyDown={onKeyDown}
    >
      <div className={styles.menuHeading}>
        <button
          ref={closeRef}
          type="button"
          aria-label="Close navigation menu"
          className={styles.closeButton}
          onClick={close}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 5L19 19M19 5L5 19" />
          </svg>
        </button>
        <h2>MENU</h2>
      </div>
      <div className={styles.links}>
        <PloSwitch forHeader={false} />
        {menuLinks.filter((link) => !link.external).map((link) => (
          <Link
            key={link.name}
            to={`${link.linkTo}?isPlo5=${isPlo5}`}
            onClick={() => setVisible(false)}
          >
            {link.name}
          </Link>
        ))}
        {menuLinks.filter((link) => link.name === 'Discord').map((link) => (
          <a
            key={link.name}
            href={link.linkTo}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setVisible(false)}
          >
            <img src={blackDiscord} width="28" height="19" alt="" />
            {link.name}
          </a>
        ))}
      </div>
    </nav>
  );
};

export default MobileMenu;
